import React, { useMemo, useRef, useState } from "react";
import apiFetch from "../../utils/apiFetch";
import PdfSchemeModal from "../schemePdfModal/PdfSchemeModal";
import { ConfirmModal } from "./SmModal";

const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";
const MAX_PDF_FILES = 10;
const MAX_PDF_SIZE = 50 * 1024 * 1024;

const getMediaId = (scheme) =>
  typeof scheme?.media === "object" ? scheme.media?.id : scheme?.media;

const getSchemeTitle = (scheme, index) =>
  String(scheme?.title || "").trim() || `Схема ${index + 1}`;

const getPdfFileName = (title) =>
  title.toLowerCase().endsWith(".pdf") ? title : `${title}.pdf`;

const isPdfScheme = (scheme) => scheme?.mime_type === "application/pdf";

function PdfFileIcon() {
  return (
    <svg className="sm-scheme-card__pdf-icon" viewBox="0 0 72 88" aria-hidden="true">
      <path d="M13 2h31l15 15v67H13z" />
      <path d="M44 2v17h15" />
      <rect x="5" y="48" width="62" height="25" rx="6" />
      <text x="36" y="65" textAnchor="middle">PDF</text>
    </svg>
  );
}

export default function SchemesTab({ substationId, schemes = [], canCreate, canDelete, onRefresh }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfIndex, setPdfIndex] = useState(0);

  const pdfItems = useMemo(
    () =>
      schemes
        .map((scheme, index) => ({ scheme, index }))
        .filter(({ scheme }) => isPdfScheme(scheme) && getMediaId(scheme))
        .map(({ scheme, index }) => {
          const mediaId = getMediaId(scheme);
          const url = `${DIRECTUS_URL}/assets/${mediaId}`;
          const title = getSchemeTitle(scheme, index);
          return {
            id: scheme.id || mediaId,
            title,
            url,
            downloadUrl: `${DIRECTUS_URL}/assets/${mediaId}/${encodeURIComponent(getPdfFileName(title))}?download`,
          };
        }),
    [schemes]
  );

  const pdfIndexById = useMemo(
    () => new Map(pdfItems.map((item, index) => [item.id, index])),
    [pdfItems]
  );

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (files.length > MAX_PDF_FILES) {
      alert(`Бир мартада энг кўпи ${MAX_PDF_FILES} та PDF файл юклаш мумкин.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    for (const f of files) {
      const hasPdfExtension = f.name.toLowerCase().endsWith(".pdf");
      const hasValidMime = !f.type || f.type === "application/pdf";
      if (!hasPdfExtension || !hasValidMime) {
        alert(`"${f.name}" PDF форматда эмас. Фақат PDF файл юклаш мумкин.`);
        if (fileRef.current) fileRef.current.value = "";
        return;
      }

      if (f.size > MAX_PDF_SIZE) {
        alert(`"${f.name}" ҳажми ${(f.size / 1024 / 1024).toFixed(1)} МБ — максимум 50 МБ`);
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("fileNames", JSON.stringify(files.map((f) => f.name)));
      for (const f of files) fd.append("files", f);
      const res = await apiFetch(`/api/substation-management/${substationId}/schemes`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).message);
      onRefresh();
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDelete() {
    try {
      const res = await apiFetch(`/api/substation-management/${substationId}/schemes/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).message);
      setDeleteTarget(null);
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="sm-schemes-panel">
      <div className="sm-equip__header">
        <h3 className="sm-equip__title">
          Схемалар <span className="sm-equip__count">({schemes.length})</span>
        </h3>
      </div>

      <div className="sm-schemes">
        {schemes.map((s, schemeIndex) => {
          const mediaId = getMediaId(s);
          const title = getSchemeTitle(s, schemeIndex);
          const itemId = s.id || mediaId || `scheme-${schemeIndex}`;

          if (isPdfScheme(s)) {
            const itemIndex = pdfIndexById.get(s.id || mediaId);
            const pdfItem = Number.isInteger(itemIndex) ? pdfItems[itemIndex] : null;

            return (
              <article key={itemId} className="sm-scheme-card sm-scheme-card--pdf">
                <div className="sm-scheme-card__pdf-preview">
                  <PdfFileIcon />
                  <span className="sm-scheme-card__pdf-badge">PDF схема</span>
                </div>
                <div className="sm-scheme-card__title" title={title}>{title}</div>
                <div className="sm-scheme-card__actions">
                  <button
                    type="button"
                    className="sm-scheme-card__action sm-scheme-card__action--view"
                    disabled={!pdfItem}
                    onClick={() => {
                      if (!pdfItem) return;
                      setPdfIndex(itemIndex);
                      setPdfOpen(true);
                    }}
                  >
                    Кўриш
                  </button>
                  {pdfItem && (
                    <a
                      className="sm-scheme-card__action"
                      href={pdfItem.downloadUrl}
                      download={getPdfFileName(title)}
                    >
                      Юклаб олиш
                    </a>
                  )}
                </div>
                {canDelete && (
                  <button
                    type="button"
                    className="sm-scheme-card__delete"
                    onClick={() => setDeleteTarget(s)}
                    aria-label={`Ўчириш: ${title}`}
                    title="Ўчириш"
                  >
                    &#10005;
                  </button>
                )}
              </article>
            );
          }

          return (
            <article key={itemId} className="sm-scheme-card">
              <img
                className="sm-scheme-card__img"
                src={`${DIRECTUS_URL}/assets/${mediaId}?fit=cover&width=360&height=280`}
                alt={title}
                onError={(e) => {
                  e.target.src =
                    "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='110'><rect width='100%' height='100%' fill='%23f1f5f9'/><text x='50%' y='50%' text-anchor='middle' dy='.3em' fill='%2394a3b8' font-size='13'>File</text></svg>";
                }}
              />
              <div className="sm-scheme-card__title" title={title}>{title}</div>
              {canDelete && (
                <button
                  type="button"
                  className="sm-scheme-card__delete"
                  onClick={() => setDeleteTarget(s)}
                  aria-label={`Ўчириш: ${title}`}
                  title="Ўчириш"
                >
                  &#10005;
                </button>
              )}
            </article>
          );
        })}

        {schemes.length === 0 && !canCreate && <div className="sm-detail-empty">Схемалар топилмади</div>}

        {canCreate && (
          <button
            type="button"
            className="sm-upload-zone"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            <span className="sm-upload-zone__icon">&#128206;</span>
            <span className="sm-upload-zone__text">{uploading ? "Юкланмоқда..." : "Схема юклаш"}</span>
            <span className="sm-upload-zone__hint">PDF, 50 МБ гача, 10 тагача</span>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,application/pdf"
              onChange={handleUpload}
            />
          </button>
        )}
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Ўчириш"
          text="Бу схемани ўчирмоқчимисиз?"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <PdfSchemeModal
        open={pdfOpen}
        items={pdfItems}
        index={pdfIndex}
        onClose={() => setPdfOpen(false)}
        onChangeIndex={setPdfIndex}
      />
    </div>
  );
}
